package com.evoapps.evoshift.domain.market.payroll.poland

import com.evoapps.evoshift.domain.legal.LegalCalendars
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.Month
import kotlinx.datetime.plus

/**
 * Public holidays in Poland (ustawa o dniach wolnych od pracy) and the monthly working-time norm
 * (Kodeks pracy art. 130): 8 hours for every Monday–Friday, minus 8 hours for every holiday that
 * falls on a day other than Sunday. Christmas Eve is a holiday from 2025.
 *
 * SH-55: the holidays come from the legal calendar (firebase/legal/PL/{year}.json, shipped with the
 * app as [LegalCalendars]) for the years it lists; other years use the statute's rules below.
 * LegalCalendarIntegrityTest keeps both identical for the listed years.
 */
object PolishCalendar {
    private val listed: Map<Int, Set<LocalDate>> by lazy {
        LegalCalendars.all.values.filter { it.country == "PL" }.associate { it.year to it.holidayDates }
    }

    fun holidays(year: Int): Set<LocalDate> = listed[year] ?: statutoryHolidays(year)

    /** The holidays of ustawa o dniach wolnych od pracy art. 1 pkt 1, by its rules. */
    fun statutoryHolidays(year: Int): Set<LocalDate> {
        val easter = easterSunday(year)
        val fixed = buildList {
            add(LocalDate(year, Month.JANUARY, 1))   // Nowy Rok
            add(LocalDate(year, Month.JANUARY, 6))   // Trzech Króli
            add(LocalDate(year, Month.MAY, 1))       // Święto Pracy
            add(LocalDate(year, Month.MAY, 3))       // Święto Konstytucji 3 Maja
            add(LocalDate(year, Month.AUGUST, 15))   // Wniebowzięcie NMP
            add(LocalDate(year, Month.NOVEMBER, 1))  // Wszystkich Świętych
            add(LocalDate(year, Month.NOVEMBER, 11)) // Święto Niepodległości
            if (year >= 2025) add(LocalDate(year, Month.DECEMBER, 24)) // Wigilia
            add(LocalDate(year, Month.DECEMBER, 25))
            add(LocalDate(year, Month.DECEMBER, 26))
        }
        val movable = listOf(
            easter,                              // Wielkanoc
            easter.plus(DatePeriod(days = 1)),   // Poniedziałek Wielkanocny
            easter.plus(DatePeriod(days = 49)),  // Zielone Świątki
            easter.plus(DatePeriod(days = 60))   // Boże Ciało
        )
        return (fixed + movable).toSet()
    }

    fun isHoliday(date: LocalDate) = date in holidays(date.year)

    /** Full-time working hours in the month. */
    fun workingHours(year: Int, month: Int): Int {
        val first = LocalDate(year, month, 1)
        return workingHours(first, first.plus(DatePeriod(months = 1)).plus(DatePeriod(days = -1)))
    }

    /**
     * Full-time working hours from [start] to [endInclusive] (part of a month, SH-48), counted day
     * by day with the monthly rule: 8 hours for each Monday–Friday, minus 8 for each holiday in the
     * range that falls on a day other than Sunday. Over a whole month it is [workingHours]; a range
     * that ends before it starts has none.
     */
    fun workingHours(start: LocalDate, endInclusive: LocalDate): Int {
        var date = start
        var hours = 0
        var holidays = holidays(date.year)
        while (date <= endInclusive) {
            if (date.month == Month.JANUARY && date.day == 1) holidays = holidays(date.year)
            val weekday = date.dayOfWeek != DayOfWeek.SATURDAY && date.dayOfWeek != DayOfWeek.SUNDAY
            if (weekday) hours += 8
            if (date in holidays && date.dayOfWeek != DayOfWeek.SUNDAY) hours -= 8
            date = date.plus(DatePeriod(days = 1))
        }
        return hours
    }

    /** Gregorian Easter Sunday (anonymous Gregorian algorithm). */
    fun easterSunday(year: Int): LocalDate {
        val a = year % 19
        val b = year / 100
        val c = year % 100
        val d = b / 4
        val e = b % 4
        val f = (b + 8) / 25
        val g = (b - f + 1) / 3
        val h = (19 * a + b - d - g + 15) % 30
        val i = c / 4
        val k = c % 4
        val l = (32 + 2 * e + 2 * i - h - k) % 7
        val m = (a + 11 * h + 22 * l) / 451
        val month = (h + l - 7 * m + 114) / 31
        val day = (h + l - 7 * m + 114) % 31 + 1
        return LocalDate(year, month, day)
    }
}
